/**
 * CreateBillModal - Modal for creating new vendor bills with PO linking
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { vendorBillsAPI, vendorsAPI, purchaseOrdersAPI } from '../../utils/api';
import { toast } from 'sonner';
import { Loader2, Search, ShoppingBag, X, Link as LinkIcon } from 'lucide-react';

// Format currency for display
const formatCurrency = (cents) => {
  if (cents === null || cents === undefined) return '$0.00';
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const CreateBillModal = ({ open, onOpenChange, onSuccess, initialVendorId, initialVendorName, initialPoId, initialPoNumber }) => {
  const [formData, setFormData] = useState({
    vendor_id: '',
    vendor_name: '',
    po_id: '',
    po_number: '',
    invoice_number: '',
    amount: '',
    due_date: '',
    notes: '',
  });
  const [vendors, setVendors] = useState([]);
  const [vendorSearch, setVendorSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  
  // PO search state
  const [poSearch, setPoSearch] = useState('');
  const [poSearchResults, setPoSearchResults] = useState([]);
  const [searchingPOs, setSearchingPOs] = useState(false);
  
  const [submitting, setSubmitting] = useState(false);

  // Fetch vendors
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const res = await vendorsAPI.getVendors({ page_size: 100, is_active: true });
        setVendors(res.vendors || []);
      } catch (err) {
        console.error('Failed to fetch vendors:', err);
      }
    };
    if (open) fetchVendors();
  }, [open]);

  // Search vendors
  useEffect(() => {
    if (vendorSearch.length >= 2) {
      const results = vendors.filter(v => 
        v.name.toLowerCase().includes(vendorSearch.toLowerCase())
      );
      setSearchResults(results.slice(0, 10));
    } else {
      setSearchResults([]);
    }
  }, [vendorSearch, vendors]);

  // Initialize with props when modal opens
  useEffect(() => {
    if (open) {
      setFormData({
        vendor_id: initialVendorId || '',
        vendor_name: initialVendorName || '',
        po_id: initialPoId || '',
        po_number: initialPoNumber || '',
        invoice_number: '',
        amount: '',
        due_date: '',
        notes: '',
      });
      setVendorSearch('');
      setSearchResults([]);
      setPoSearch('');
      setPoSearchResults([]);
    }
  }, [open, initialVendorId, initialVendorName, initialPoId, initialPoNumber]);

  // Search POs for the selected vendor
  const searchPOs = async (query) => {
    if (!formData.vendor_id || query.length < 2) {
      setPoSearchResults([]);
      return;
    }
    
    setSearchingPOs(true);
    try {
      // Search POs for this vendor
      const data = await purchaseOrdersAPI.getPurchaseOrders({
        vendor_id: formData.vendor_id,
        q: query,
        page_size: 10,
      });
      setPoSearchResults(data.purchase_orders || data.items || []);
    } catch (err) {
      console.error('Failed to search POs:', err);
    } finally {
      setSearchingPOs(false);
    }
  };

  const handleSelectVendor = (vendor) => {
    // When vendor changes, clear PO selection (vendor match enforcement)
    setFormData(prev => ({ 
      ...prev, 
      vendor_id: vendor.id, 
      vendor_name: vendor.name,
      po_id: '',
      po_number: ''
    }));
    setVendorSearch('');
    setSearchResults([]);
    setPoSearch('');
    setPoSearchResults([]);
  };

  const handleSelectPO = (po) => {
    setFormData(prev => ({ 
      ...prev, 
      po_id: po.id, 
      po_number: po.po_number 
    }));
    setPoSearch('');
    setPoSearchResults([]);
  };

  const handleUnlinkPO = () => {
    setFormData(prev => ({ ...prev, po_id: '', po_number: '' }));
    setPoSearch('');
    setPoSearchResults([]);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.vendor_id) {
      toast.error('Please select a vendor');
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setSubmitting(true);
    try {
      // Convert dollars to cents and map field names to backend schema
      const amountCents = Math.round(parseFloat(formData.amount) * 100);
      const result = await vendorBillsAPI.createBill({
        vendor_id: formData.vendor_id,
        po_id: formData.po_id || null,
        bill_number: formData.invoice_number || null,  // Vendor's invoice number
        bill_date: new Date().toISOString().split('T')[0],  // Today's date
        due_date: formData.due_date || null,
        memo: formData.notes || null,
        amount_cents: amountCents,
      });
      const bill = result.bill || result;
      toast.success(`Bill ${bill.bill_number || bill.id} created`);
      if (result.warning) {
        toast.warning(result.warning);
      }
      onOpenChange(false);
      onSuccess?.(bill.id);
    } catch (err) {
      // Handle structured error responses
      const errorData = err.data || {};
      const errorMessage = errorData.detail || err.message || 'Failed to create bill';
      toast.error(errorMessage);
      
      // Log error code for debugging
      if (errorData.code) {
        console.error(`Bill creation error: ${errorData.code}`, errorData);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="create-bill-modal">
        <DialogHeader>
          <DialogTitle>Create New Vendor Bill</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Vendor Selection */}
          <div className="space-y-2">
            <Label>Vendor *</Label>
            {formData.vendor_id ? (
              <div className="flex items-center justify-between p-3 bg-gray-50 border rounded-lg">
                <span className="font-medium">{formData.vendor_name}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setFormData(prev => ({ ...prev, vendor_id: '', vendor_name: '', po_id: '', po_number: '' }))}
                >
                  Change
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={vendorSearch}
                    onChange={(e) => setVendorSearch(e.target.value)}
                    placeholder="Search vendors..."
                    className="pl-10"
                    data-testid="vendor-search-input"
                  />
                </div>
                {searchResults.length > 0 && (
                  <div className="border rounded-lg max-h-40 overflow-y-auto">
                    {searchResults.map(v => (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => handleSelectVendor(v)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-b-0"
                      >
                        {v.name}
                      </button>
                    ))}
                  </div>
                )}
                {vendors.length > 0 && !vendorSearch && (
                  <select
                    value=""
                    onChange={(e) => {
                      const v = vendors.find(vendor => vendor.id === e.target.value);
                      if (v) handleSelectVendor(v);
                    }}
                    className="w-full h-10 px-3 border rounded-md bg-white text-sm"
                  >
                    <option value="">Or select from list...</option>
                    {vendors.map(v => (
                      <option key={v.id} value={v.id}>{v.name}</option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>

          {/* PO Linking (only show if vendor is selected) */}
          {formData.vendor_id && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <LinkIcon className="w-3 h-3" />
                Link to Purchase Order (optional)
              </Label>
              {formData.po_id ? (
                <div className="flex items-center justify-between p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <ShoppingBag className="w-4 h-4 text-purple-600" />
                    <span className="font-mono font-medium">{formData.po_number}</span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleUnlinkPO}
                    className="text-red-600 hover:text-red-700"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      value={poSearch}
                      onChange={(e) => {
                        setPoSearch(e.target.value);
                        searchPOs(e.target.value);
                      }}
                      placeholder="Search PO by number..."
                      className="pl-10"
                      data-testid="po-search-input"
                    />
                  </div>
                  {searchingPOs && (
                    <div className="text-sm text-gray-500 flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Searching...
                    </div>
                  )}
                  {poSearchResults.length > 0 && (
                    <div className="border rounded-lg max-h-40 overflow-y-auto">
                      {poSearchResults.map(po => (
                        <button
                          key={po.id}
                          type="button"
                          onClick={() => handleSelectPO(po)}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-b-0 flex justify-between items-center"
                        >
                          <div>
                            <p className="font-mono font-medium">{po.po_number}</p>
                            <p className="text-xs text-gray-500">
                              <Badge variant="outline" className="text-xs">{po.status}</Badge>
                            </p>
                          </div>
                          <span className="text-sm tabular-nums">{formatCurrency(po.total_cents)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-gray-500">
                    Search for a PO from this vendor to link to the bill
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Bill Number</Label>
              <Input
                value={formData.invoice_number}
                onChange={(e) => handleChange('invoice_number', e.target.value)}
                placeholder="INV-001"
              />
            </div>
            <div className="space-y-2">
              <Label>Amount *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) => handleChange('amount', e.target.value)}
                placeholder="0.00"
                data-testid="bill-amount-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Due Date</Label>
            <Input
              type="date"
              value={formData.due_date}
              onChange={(e) => handleChange('due_date', e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Additional notes..."
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !formData.vendor_id || !formData.amount}>
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</>
              ) : (
                'Create Bill'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateBillModal;
